/**
 * Tax calculation constants and utilities for Australia
 */

// GST rate for Australia (9% as per your requirement)
export const GST_RATE = 0.09;
export const GST_PERCENTAGE = 9;

export interface PricingBreakdown {
  subtotal: number;
  gst: number;
  total: number;
  subtotalDisplay: number;
  gstDisplay: number;
  totalDisplay: number;
}

/**
 * Calculate GST amount from subtotal
 * @param subtotal - Amount before GST
 * @returns GST amount
 */
export const calculateGST = (subtotal: number): number => {
  return subtotal * GST_RATE;
};

/**
 * Calculate total amount including GST
 * @param subtotal - Amount before GST
 * @returns Total amount including GST
 */
export const calculateTotalWithGST = (subtotal: number): number => {
  return subtotal + calculateGST(subtotal);
};

/**
 * Break down pricing with GST
 * @param amount - Base amount
 * @param price - Price per unit
 * @returns Object with subtotal, GST, and total
 */
export const calculatePricingBreakdown = (amount: number, price: number) => {
  const subtotal = amount * price;
  const gst = calculateGST(subtotal);
  const total = calculateTotalWithGST(subtotal);

  return {
    subtotal,
    gst,
    total,
    // Round to 2 decimal places for display
    subtotalDisplay: Math.round(subtotal * 100) / 100,
    gstDisplay: Math.round(gst * 100) / 100,
    totalDisplay: Math.round(total * 100) / 100,
  };
};

/**
 * Format pricing breakdown for display
 * @param amount - Base amount
 * @param price - Price per unit
 * @returns Formatted string breakdown
 */
export const formatPricingBreakdown = (amount: number, price: number) => {
  const breakdown = calculatePricingBreakdown(amount, price);

  return {
    ...breakdown,
    formatted: {
      subtotal: `$${breakdown.subtotalDisplay.toFixed(2)}`,
      gst: `$${breakdown.gstDisplay.toFixed(2)}`,
      total: `$${breakdown.totalDisplay.toFixed(2)}`,
    }
  };
};